import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { X, Search } from 'lucide-react';
import { KINDS, COUNTERPARTS } from '@/lib/interactions';
import { localISODate } from '@/lib/payments';

const API = `${window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || ''}/api`;

const blank = (prefill = {}) => ({
  date: localISODate(), kind: 'reunion', counterpart_type: 'otro', contact_name: '', contact_role: '',
  topic_id: 'general', text: '', player_id: '', player_name: '', about_name: '', club: '',
  next_step: '', next_step_date: '', ...prefill,
});

/**
 * Alta y edición de una entrada de "Reuniones y notas".
 * - `entry`: entrada a editar (null para una nueva). `prefill`: datos iniciales de una nueva (ej. el jugador).
 * - Una entrada puede ir ligada a un jugador con ficha, a un club, o ser una nota suelta; si el jugador
 *   todavía no tiene ficha se escribe su nombre.
 */
const InteractionDialog = ({ open, onOpenChange, entry, prefill, topics, clubs, onSaved }) => {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setForm(blank({
        date: entry.date, kind: entry.kind, counterpart_type: entry.counterpart_type, contact_name: entry.contact_name || '',
        contact_role: entry.contact_role || '', topic_id: entry.topic_id || 'general', text: entry.text,
        player_id: entry.player_id || '', player_name: entry.player_name || '', about_name: entry.about_name || '',
        club: entry.club || '', next_step: entry.next_step || '', next_step_date: entry.next_step_date || '',
      }));
    } else {
      setForm(blank(prefill || {}));
    }
    setQuery('');
    setResults([]);
  }, [open, entry, prefill]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const search = (value) => {
    setQuery(value);
    clearTimeout(timer.current);
    if (value.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/players`, { params: { search: value.trim(), limit: 8 } });
        setResults(res.data.data || []);
      } catch (_) { setResults([]); }
    }, 250);
  };

  const pickPlayer = (p) => {
    setForm((f) => ({ ...f, player_id: p.id, player_name: `${p.first_name} ${p.last_name}`, about_name: '', club: f.club || p.origin_club || '' }));
    setQuery('');
    setResults([]);
  };

  const save = async () => {
    if (!form.text.trim()) { toast.error('Escribí la nota'); return; }
    setSaving(true);
    const payload = {
      date: form.date || null, kind: form.kind, counterpart_type: form.counterpart_type,
      contact_name: form.contact_name, contact_role: form.contact_role, topic_id: form.topic_id, text: form.text,
      player_id: form.player_id || null, about_name: form.player_id ? '' : form.about_name, club: form.club,
      next_step: form.next_step, next_step_date: form.next_step_date || null,
    };
    try {
      if (entry) await axios.put(`${API}/interactions/${entry.id}`, payload);
      else await axios.post(`${API}/interactions`, payload);
      toast.success(entry ? 'Entrada actualizada' : 'Entrada guardada');
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const isParent = form.counterpart_type === 'padre_madre';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="interaction-dialog">
        <DialogHeader>
          <DialogTitle>{entry ? 'Editar entrada' : 'Nueva entrada'}</DialogTitle>
          <DialogDescription>Registrá una reunión, charla, llamada o mensaje. Solo la ves vos (y el administrador).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Fecha</Label>
            <Input type="date" max={localISODate()} value={form.date} onChange={(e) => set('date', e.target.value)} data-testid="int-date" />
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={form.kind} onValueChange={(v) => set('kind', v)}>
              <SelectTrigger data-testid="int-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Con quién</Label>
            <Select value={form.counterpart_type} onValueChange={(v) => set('counterpart_type', v)}>
              <SelectTrigger data-testid="int-counterpart"><SelectValue /></SelectTrigger>
              <SelectContent>{COUNTERPARTS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tema</Label>
            <Select value={form.topic_id} onValueChange={(v) => set('topic_id', v)}>
              <SelectTrigger data-testid="int-topic"><SelectValue /></SelectTrigger>
              <SelectContent>{topics.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nombre de la persona</Label>
            <Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} placeholder="Ej: Pablo Pérez" data-testid="int-contact-name" />
          </div>
          <div>
            <Label>Cargo</Label>
            <Input value={form.contact_role} onChange={(e) => set('contact_role', e.target.value)} placeholder="Ej: Técnico Sub-15" data-testid="int-contact-role" />
          </div>
        </div>

        <div className="space-y-2 border rounded-md p-3 bg-slate-50">
          <Label className="font-semibold">{isParent ? 'Jugador del que se habló' : 'Jugador (opcional)'}</Label>
          {form.player_id ? (
            <div className="flex items-center justify-between rounded-md bg-white border px-3 py-2 text-sm" data-testid="int-player-selected">
              <span>{form.player_name}</span>
              <button type="button" onClick={() => set('player_id', '')} className="text-slate-500 hover:text-red-600" aria-label="Quitar jugador"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
                <Input className="pl-8" value={query} onChange={(e) => search(e.target.value)} placeholder="Buscar jugador con ficha por nombre" data-testid="int-player-search" />
                {results.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full rounded-md border bg-white shadow max-h-48 overflow-y-auto">
                    {results.map((p) => (
                      <button type="button" key={p.id} onClick={() => pickPlayer(p)} className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100" data-testid={`int-player-result-${p.id}`}>
                        {p.first_name} {p.last_name}{p.origin_club ? <span className="text-slate-500"> · {p.origin_club}</span> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Input value={form.about_name} onChange={(e) => set('about_name', e.target.value)} placeholder="O escribí su nombre si todavía no tiene ficha" data-testid="int-about-name" />
            </>
          )}
          <div>
            <Label>Club (opcional)</Label>
            <Input list="int-clubs" value={form.club} onChange={(e) => set('club', e.target.value)} placeholder="Elegí uno o escribí uno nuevo" data-testid="int-club" />
            <datalist id="int-clubs">{clubs.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <p className="text-xs text-slate-500">Podés dejarlo sin jugador ni club y queda como nota tuya.</p>
        </div>

        <div>
          <Label>Nota *</Label>
          <Textarea rows={5} value={form.text} onChange={(e) => set('text', e.target.value)} placeholder="Qué se habló, qué se acordó…" data-testid="int-text" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label>Próximo paso (opcional)</Label>
            <Input value={form.next_step} onChange={(e) => set('next_step', e.target.value)} placeholder="Ej: Llamar al técnico" data-testid="int-next-step" />
          </div>
          <div>
            <Label>Fecha</Label>
            <Input type="date" value={form.next_step_date} onChange={(e) => set('next_step_date', e.target.value)} data-testid="int-next-date" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-brand-600 hover:bg-brand-700" data-testid="int-save">{saving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InteractionDialog;
